import { useState, useEffect } from 'react'
import { ApiClient, apiClients } from '@/shared/services/api'

interface Model {
  id: string
  name: string
  description: string
  provider?: string
  provider_name?: string
}

interface Provider {
  id: string
  name: string
  configured: boolean
  models: Model[]
}

// 后端实际返回格式
interface ModelsApiResponse {
  default_provider: string
  default_model: string
  models: Array<{
    id: string
    name: string
    description: string
    provider: string
    provider_name: string
  }>
  providers: Array<{
    id: string
    name: string
    model_count: number
  }>
}

// 转换后的格式
interface ModelsConfig {
  default_provider: string
  default_model: string
  providers: Provider[]
}

export function useModels() {
  const [config, setConfig] = useState<ModelsConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchModels() {
      try {
        const response = await ApiClient.getWithClient<ModelsApiResponse>(
          apiClients.agentAdmin,
          '/config/llm-models'
        )
        
        console.log('📊 [Models] 后端返回数据:', JSON.stringify(response.data, null, 2))
        
        if (response.success && response.data) {
          const { default_provider, default_model, models, providers } = response.data
          
          // 按提供商分组模型
          const providerMap = new Map<string, Provider>()
          
          // 初始化提供商
          providers.forEach(p => {
            providerMap.set(p.id, {
              id: p.id,
              name: p.name,
              configured: true,
              models: [],
            })
          })
          
          // 将模型分配到对应的提供商
          models.forEach(m => {
            const provider = providerMap.get(m.provider)
            if (provider) {
              provider.models.push({
                id: m.id,
                name: m.name,
                description: m.description,
                provider: m.provider,
                provider_name: m.provider_name,
              })
            }
          })
          
          const config: ModelsConfig = {
            default_provider: default_provider || '',
            default_model: default_model || '',
            providers: Array.from(providerMap.values()),
          }
          
          console.log('✅ [Models] 解析后的配置:', JSON.stringify(config, null, 2))
          setConfig(config)
        } else {
          setError('获取模型列表失败')
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '无法连接到服务器'
        setError(errorMessage)
        console.error('❌ [Models] Failed to fetch models:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchModels()
  }, [])

  // 获取所有可用的模型（扁平化列表，按提供商分组显示）
  const allModels = config?.providers.flatMap(p => 
    p.models.map(m => ({
      ...m,
      providerId: p.id,
      providerName: p.name,
      configured: p.configured,
    }))
  ) || []

  // 获取已配置的提供商的模型（优先显示）
  const configuredModels = config?.providers
    .filter(p => p.configured)
    .flatMap(p => 
      p.models.map(m => ({
        ...m,
        providerId: p.id,
        providerName: p.name,
      }))
    ) || []

  // 按提供商分组的模型（用于下拉选择）
  const modelsByProvider = config?.providers.map(p => ({
    provider: {
      id: p.id,
      name: p.name,
      configured: p.configured,
    },
    models: p.models.map(m => ({
      ...m,
      providerId: p.id,
      providerName: p.name,
    })),
  })) || []

  return {
    config,
    providers: config?.providers || [],
    allModels,
    configuredModels,
    modelsByProvider,
    defaultProvider: config?.default_provider || '',
    defaultModel: config?.default_model || '',
    loading,
    error,
  }
}

